import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

/*
 * The ChequeGuard list-card header, for every list screen.
 *
 * On ChequeGuard the table card opens with a header row: title + subtitle on
 * the left, the search box and "N items" on the right. The other modules had
 * their search OUTSIDE the card, top-left, with no title.
 *
 * The catch: in those modules the search lives in <XxxTableTools> and the table
 * in <XxxTable> -- two sibling components, each with its own (different) search
 * handler and fetch thunk. Moving 50+ search handlers into the tables would
 * touch every module's data flow. Instead the header is PORTALED:
 *
 *   <AdaptableCard>                  provides a ListCardContext
 *     <XxxTableTools>                renders <TableCardHeader title=…>{search}</…>
 *     <XxxTable> -> <DataTable>      renders the slot at the top of its card
 *
 * The search element and its handler stay exactly where they were; only where
 * it is painted changes. React portals keep context and events, so redux, the
 * search ref and the debounce all behave as before.
 *
 * If there is no slot (no provider, or a screen whose list is not the shared
 * DataTable), TableCardHeader renders in place as a card top, so nothing ever
 * disappears.
 */

export const ListCardContext = createContext(null);

export const ListCardProvider = ({ children }) => {
  const [slot, setSlotState] = useState(null);
  const [total, setTotal] = useState(null);

  // First table to mount claims the slot; a second table on the same card
  // must not steal the header from the first.
  const registerSlot = useCallback((el) => {
    setSlotState((prev) => (el ? prev || el : null));
  }, []);

  const value = useMemo(
    () => ({ slot, registerSlot, total, setTotal }),
    [slot, registerSlot, total]
  );

  return (
    <ListCardContext.Provider value={value}>{children}</ListCardContext.Provider>
  );
};

/** The header row markup itself -- shared with components/shared/table/DataTable. */
export const TableCardHeaderRow = ({ title, subtitle, count, children }) => (
  <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
      {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
    </div>
    <div className="flex flex-wrap items-center gap-3">
      {/* Modules wrap their search / date range / filters in their own divs
          (some with no gap, some with mb-4). .cg-card-toolbar (app.css) lays
          them out the same way everywhere: one row, equal gaps, centred. */}
      {children && <div className="cg-card-toolbar">{children}</div>}
      {count !== undefined && count !== null && (
        <div className="text-xs text-gray-500 whitespace-nowrap">{count}</div>
      )}
    </div>
  </div>
);

const TableCardHeader = ({ title, subtitle, children }) => {
  const listCard = useContext(ListCardContext);
  const total = listCard?.total;
  const count = typeof total === "number" ? `${total} items` : null;

  const row = (
    <TableCardHeaderRow title={title} subtitle={subtitle} count={count}>
      {children}
    </TableCardHeaderRow>
  );

  if (listCard?.slot) {
    return createPortal(row, listCard.slot);
  }

  // No shared table to sit in: render as the top of a card, in place.
  return (
    <div className="w-full min-w-0 bg-white border border-gray-200 rounded-t-lg">
      {row}
    </div>
  );
};

export default TableCardHeader;
